import { Component, OnInit, inject } from '@angular/core';
import { serverErrorMessage } from '@core/helpers';
import { HelperFunctionsService } from '@core/services/helper-functions.service';
import { FormBuilder, FormGroup, Validators, FormControl, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { CoreConfigService } from '@core/services/config.service';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { PaymentService } from '@core/services/payment.service';
import { SubscriptionPlan } from '@core/models/subscription';
import { ActivatedRoute } from '@angular/router';
import { COUNTIES, County } from '@core/constants/consts';
import { School } from '@core/models/school';
import { environment } from 'environments/environment';
import { HttpClient } from '@angular/common/http';

@Component({
  standalone: false,
  selector: 'app-request-exams',
  templateUrl: './request-exams-form.component.html',
  styleUrls: ['./request-exams-form.component.scss']
})
export class RequestExamFormComponent implements OnInit {
    private _helpers = inject(HelperFunctionsService);
    public coreConfig: any;
    public requestForm: FormGroup;
    public formOptions: SubscriptionPlan[] = [];
    public isSubmitting = false;
    public isRM = false;
    public isFree = false;
    public planType = 'FREE_ACHIEVERS_EXAM';
    public counties: County[] = COUNTIES.sort((a, b) => a.name.localeCompare(b.name));
    public schools: School[] = [];
    public filteredSchools: School[] = [];
    public selectedSchool: School | null = null;
    public selectedCounty: County | null = null;
    public loadingSchools = false;
    public showSchoolDropdown = false;
    public schoolSearchControl = new FormControl('');
  
    // Private
    private _unsubscribeAll: Subject<any>;
  
    constructor(
      private _coreConfigService: CoreConfigService,
      private fb: FormBuilder,
      private toastr: ToastrService,
      private paymentService: PaymentService,
      private route: ActivatedRoute,
      private http: HttpClient,
    ) {
      this._unsubscribeAll = new Subject();
  
      // Configure the layout
      this._coreConfigService.config = {
        layout: {
          navbar: {
            hidden: true
          },
          footer: {
            hidden: true
          },
          menu: {
            hidden: true
          },
          customizer: false,
          enableLocalStorage: false
        }
      };
    }
  
    private zerakiEmailValidator(): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
          return null;
        }
        return control.value.toLowerCase().endsWith('@zeraki.app') 
          ? null 
          : { zerakiEmail: true };
      };
    }

    ngOnInit(): void {
      // Check for isRM parameter
      this.isRM = this.route.snapshot.queryParams['isRM'] === 'true';
      this.isFree = this.route.snapshot.queryParams['isFree'] === 'true';
      this.planType = this.isFree ? 'FREE_ACHIEVERS_EXAM' : 'ACHIEVERS_EXAM';

      // Subscribe to config changes
      this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
        this.coreConfig = config;
      });

      // Initialize the form with all fields
      this.requestForm = this.fb.group({
        schoolName: [null, this.isRM ? [] : [Validators.required]],
        schoolId: [null, this.isRM ? [Validators.required] : []],
        countyId: [null, this.isRM ? [Validators.required] : []],
        managerEmail: [null, this.isRM ? [
          Validators.required, 
          Validators.email,
          this.zerakiEmailValidator()
        ] : []],
        fullName: [null, [Validators.required]],
        email: [null, [Validators.required, Validators.email]],
        planIds: [[], [Validators.required, Validators.minLength(1)]],
        referralCode: [null],
        phoneNumber: [null, [Validators.required, Validators.pattern('^[0-9]{10}$')]],
        planType: [this.planType]
      });

      // Load exam plans
      this.paymentService.getAchieversExamPlans(this.planType).subscribe({
        next: (res: SubscriptionPlan[]) => {
          this.formOptions = res;
        },
        error: (error) => {
          this.toastr.error('Failed to load exam plans. Please try again later.');
          console.error('Error loading exam plans:', error);
        }
      });

      // If isRM is true, make schoolName not required initially
      if (this.isRM) {
        this.requestForm.get('schoolName').clearValidators();
        this.requestForm.get('schoolId').setValidators([Validators.required]);
        this.requestForm.get('countyId').setValidators([Validators.required]);
      }
    }

    onCountyChange() {
      this.selectedSchool = null;
      this.schools = [];
      this.requestForm.get('schoolId')?.setValue('');
      this.schoolSearchControl.setValue('');
      this.filteredSchools = [];
      this.showSchoolDropdown = false;
      
      const countyId = this.requestForm.get('countyId')?.value;
      if (countyId) {
        this.loadingSchools = true;
        this.http.get<any[]>(`${environment.apiUrl}/check-account?countyId=${countyId}`).subscribe({
          next: (response) => {
            // Map the response to match our School interface
            this.schools = response
              .filter(school => school && school.name) // Filter out invalid entries
              .map(school => ({
                schoolId: school.id,
                schoolName: school.name,
                students: 0,
                users: 0
              }))
              .sort((a, b) => a.schoolName.localeCompare(b.schoolName));
            
            this.filteredSchools = this.schools;
            this.loadingSchools = false;
            this.showSchoolDropdown = this.schools.length > 0;
          },
          error: () => {
            this.schools = [];
            this.filteredSchools = [];
            this.loadingSchools = false;
            this.toastr.error('Failed to load schools. Please try again later.');
          }
        });
      }
    }

    onSchoolInput(): void {
      const searchTerm = this.schoolSearchControl.value?.toLowerCase().trim();
      console.log(searchTerm);
      if (searchTerm) {
        this.showSchoolDropdown = true;
        this.filteredSchools = this.schools.filter(school => 
          school.schoolName.toLowerCase().includes(searchTerm)
        );
        console.log(this.filteredSchools);
      } else {
        this.filteredSchools = this.schools;
        this.showSchoolDropdown = this.schools.length > 0;
      }
    }

    onSchoolBlur(): void {
      // Small delay to allow click event on school selection
      setTimeout(() => {
        this.showSchoolDropdown = false;
      }, 200);
    }

    selectSchool(school: School): void {
      this.schoolSearchControl.setValue(school.schoolName);
      this.requestForm.patchValue({
        schoolName: school.schoolName,
        schoolId: school.schoolId
      });
      this.showSchoolDropdown = false;
    }
  
    ngOnDestroy(): void {
      this._unsubscribeAll.next(true);
      this._unsubscribeAll.complete();
    }

    isFormSelected(planId: number): boolean {
      const planIds = this.requestForm.get('planIds').value || [];
      return planIds.includes(planId);
    }

    onFormSelect(event: any, planId: number) {
      const planIds = [...(this.requestForm.get('planIds').value || [])];
      if (event.target.checked) {
        planIds.push(planId);
      } else {
        const index = planIds.indexOf(planId);
        if (index > -1) {
          planIds.splice(index, 1);
        }
      }
      this.requestForm.patchValue({ planIds });
    }

    getTotalPrice(): number {
      const planIds = this.requestForm.get('planIds').value || [];
      return planIds.reduce((total: number, planId: number) => {
        const plan = this.formOptions.find(opt => opt.planId === planId);
        return total + (plan ? (plan.hasDiscount ? plan.discountedAmount : plan.amount) : 0);
      }, 0);
    }

    onSubmit() {
      if (this.requestForm.valid) {
        this.isSubmitting = true;
        const request = {
          ...this.requestForm.value,
        };
        this.paymentService.createAchieversExamRequest(request).subscribe({
          next: (res: any) => {
            console.log('Form submitted:', this.requestForm.value);
            this.toastr.success(res.message, 'Success!');
            this.requestForm.reset();
            this.requestForm.patchValue({ planIds: [] });
            this.isSubmitting = false;
          },
          error: (error) => {
            this.isSubmitting = false;
            this._helpers.showWarningToastr(
              serverErrorMessage(error, "Couldn't send your request. Please try again.")
            );
          }
        });
      } else {
        this.toastr.error('Please fill all required fields correctly.');
      }
    }
}